import React, { useCallback, useContext } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import Dropdown from '@prisjakt/ui-dropdown-new';
import DropdownItem from '@prisjakt/ui-dropdown-new/src/item';
import { ParamsAndReturnTypes } from '@prisjakt/ui-translate/src/translate-func';

import { SortOption, SortOptionEnum } from './types';
import { Price } from '../../../../types';

import { trackPriceListSortUpdate } from '../tracking';

import * as S from './styles';

type SortOptionTranslationKey =
  | 'sort.price-excludes-shipping'
  | 'sort.price_includes_shipping';

type OwnProps = {
  compact?: boolean;
  prices: Price[];
  setCurrentSort: (currentSort: SortOption) => void;
  currentSort: SortOption;
};

const SortOptions = ({
  compact,
  prices,
  setCurrentSort,
  currentSort,
}: OwnProps) => {
  const { t } = useContext(AppContext);

  const sortOptions = [
    SortOptionEnum.PRICE,
    SortOptionEnum.PRICE_INCLUDES_SHIPPING,
  ];

  const currentSortName = t(
    'common',
    `sort.${currentSort}` as SortOptionTranslationKey
  );

  const sort = useCallback(
    (selectedSort: SortOption) => {
      if (!prices || currentSort === selectedSort) return null;

      trackPriceListSortUpdate(selectedSort);
      return setCurrentSort(selectedSort);
    },
    [currentSort, prices, setCurrentSort]
  );

  const renderSortOptionsButton = () => (
    <S.SortOptionsButton variant="text" color="primary">
      <S.ButtonText hide={compact}>{currentSortName}</S.ButtonText>{' '}
      {compact && <S.SortIconComponent />}
    </S.SortOptionsButton>
  );

  const getSortOptions = () =>
    compact
      ? sortOptions
      : sortOptions.filter(sortOption => sortOption !== currentSort);

  return (
    <S.SortOptions>
      <Dropdown
        data-test="PriceListSortDropdown"
        grow="right"
        toggleElement={renderSortOptionsButton()}
        variant="secondary"
        showArrow={!compact}
      >
        {getSortOptions().map(sortOption => {
          const title = t(
            'common',
            `sort.${sortOption}` as keyof ParamsAndReturnTypes['common']
          );

          return compact ? (
            <S.RadioItem
              key={sortOption}
              name="radioSelection"
              id={sortOption}
              checked={sortOption === currentSort}
              current={sortOption === currentSort}
              onChange={() => sort(sortOption)}
            >
              {title}
            </S.RadioItem>
          ) : (
            <DropdownItem
              key={sortOption}
              onClick={() => sort(sortOption)}
              data-test="PriceSortOption"
            >
              {title}
            </DropdownItem>
          );
        })}
      </Dropdown>
    </S.SortOptions>
  );
};

export default SortOptions;
